import * as React from 'react';
import { useMemo } from 'react';
import { SelectedFare } from '../../../DesktopFlight/types';
import { FareFamilyFragment, ResultsLegFragment, FlightClass, AviaPassengerFareFragment } from '@websky/graphql';
import { MinFlightPrice } from './MinFlightPrice/MinFlightPrice';
import { getMinPrices } from '../../../Results/utils';
import { SelectedFlightPrice } from './SelectedFlightPrice/SelectedFlightPrice';
import { getSeatsLeftCount } from './utils';
import { hasCanceledSegment } from '@shared/lib/flight';

interface Props {
	best?: boolean;
	singleFlight?: boolean;
	isSelected: boolean;
	flight: ResultsLegFragment;
	selectedFares: SelectedFare[];
	passengerFares?: AviaPassengerFareFragment[];
	preselectedFlightClass?: FlightClass;
	canceled?: boolean;
	onClear?: () => void;
	hideOnlyPrice?: boolean;
	isOrderLoading?: boolean;
}

const FlightPrice: React.FC<Props> = ({
	best,
	singleFlight,
	isSelected,
	flight,
	selectedFares,
	passengerFares,
	preselectedFlightClass,
	canceled,
	onClear,
	hideOnlyPrice,
	isOrderLoading
}) => {
	const [price, originalPrice, priceInMiles, hasSubsidizedFare] = useMemo(() => {
		const minPrice = getMinPrices(flight.pricesForFareGroups, true, selectedFares, false, preselectedFlightClass);
		const minOriginalPrice = getMinPrices(
			flight.pricesForFareGroups,
			true,
			selectedFares,
			true,
			preselectedFlightClass
		);

		return [
			minPrice[0]?.price,
			minOriginalPrice[0]?.price?.amount > minPrice[0]?.price?.amount && minOriginalPrice[0]?.price,
			minPrice[0]?.priceInMiles,
			flight.pricesForFareGroups.some(group => group.prices?.some(price => price.flight?.subsidized))
		];
	}, [flight, preselectedFlightClass]);

	const seatsLeft = !isSelected ? getSeatsLeftCount(flight) : null;

	const [fareFamily, selectedFare] = useMemo<[FareFamilyFragment, SelectedFare]>(() => {
		if (!isSelected) {
			return [null, null];
		}

		const fareFamily = flight.pricesForFareGroups[0]?.fareFamily;

		return [fareFamily, selectedFares[0]];
	}, [flight, selectedFares, isSelected]);

	if (selectedFare) {
		return (
			<SelectedFlightPrice
				fareFamily={fareFamily}
				selectedFare={selectedFare}
				passengerFares={passengerFares}
				canceled={canceled}
				hasCanceledSegment={hasCanceledSegment(flight.segments)}
				priceInMiles={priceInMiles}
				onClear={onClear}
				hideOnlyPrice={hideOnlyPrice}
				isOrderLoading={isOrderLoading}
				subsidized={hasSubsidizedFare}
				flightMessages={[]}
			/>
		);
	} else {
		return (
			<MinFlightPrice
				singleFlight={singleFlight}
				best={best}
				originalPrice={originalPrice}
				price={price}
				priceInMiles={priceInMiles}
				hideOnlyPrice={hideOnlyPrice}
				subsidized={hasSubsidizedFare}
				seatsLeft={seatsLeft}
				flightMessages={flight.segments[0].segment.messages}
			/>
		);
	}
};

export default FlightPrice;
